import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    try {
      const raw = localStorage.getItem("nexus_user");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(true);
  const [billing, setBilling] = useState(null); // { organization, plan, modules, is_internal, is_trial }
  const [billingLoading, setBillingLoading] = useState(false);

  const fetchBilling = useCallback(async () => {
    try {
      setBillingLoading(true);
      const r = await api.get("/billing/me");
      setBilling(r.data);
      return r.data;
    } catch {
      setBilling(null);
      return null;
    } finally {
      setBillingLoading(false);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("nexus_token");
    if (!token) {
      setLoading(false);
      return;
    }
    api
      .get("/auth/me")
      .then(async (r) => {
        setUser(r.data);
        localStorage.setItem("nexus_user", JSON.stringify(r.data));
        await fetchBilling();
      })
      .catch(() => {
        localStorage.removeItem("nexus_token");
        localStorage.removeItem("nexus_user");
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, [fetchBilling]);

  const login = async (email, password, mfa_code) => {
    const r = await api.post("/auth/login", { email, password, mfa_code });
    localStorage.setItem("nexus_token", r.data.token);
    localStorage.setItem("nexus_user", JSON.stringify(r.data.user));
    setUser(r.data.user);
    await fetchBilling();
    return r.data.user;
  };

  const refreshUser = useCallback(async () => {
    try {
      const r = await api.get("/auth/me");
      setUser(r.data);
      localStorage.setItem("nexus_user", JSON.stringify(r.data));
      return r.data;
    } catch {
      return null;
    }
  }, []);

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (err) { /* best-effort */ }
    localStorage.removeItem("nexus_token");
    localStorage.removeItem("nexus_user");
    localStorage.removeItem("nexus_tenant_switch");
    setUser(null);
    setBilling(null);
  };

  // Convenience derivations
  const modules = billing?.modules || [];
  const isInternal = !!billing?.is_internal;
  const isAdmin = user?.role === "admin";
  const hasModule = (m) => !m || isAdmin || isInternal || modules.includes(m);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        logout,
        billing,
        billingLoading,
        refreshBilling: fetchBilling,
        refreshUser,
        modules,
        isInternal,
        isAdmin,
        hasModule,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
